import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import DrugsOneBubbleWithEvidence, { EvidenceDrawer } from "./DrugsWithEvidence";

const AGENT_NAME = "o3-agent";
const BASE_URL = "/api-drug-agent"; // Replace with your backend URL

function Drugs() {
    const [threadId, setThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentEvidence, setCurrentEvidence] = useState(null);
    const [currentEvidenceIndex, setCurrentEvidenceIndex] = useState(0);
    const chatBoxRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!localStorage.getItem("token")) {
            navigate("/");
        }
        const createThread = async () => {
            try {
                const response = await fetch(`${BASE_URL}/agents/create-thread`, { method: "POST" });
                const data = await response.json();
                setThreadId(data.thread_id);
            } catch (error) {
                console.error("Failed to create thread:", error);
            }
        };
        createThread();
    }, [navigate]);

    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
    }, [messages, loading]);

    const handleEvidenceClick = (evidenceKey, currentEvidence) => {
        setCurrentEvidence(currentEvidence);
        setCurrentEvidenceIndex(evidenceKey);
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        setTimeout(() => {
            setCurrentEvidence(null);
            setCurrentEvidenceIndex(null);
        }, 300);
    };

    const sendMessage = async () => {
        if (!input.trim() || !threadId || loading) return;
        const userMessage = { role: "user", text: input };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setLoading(true);
        try {
            const response = await fetch(`${BASE_URL}/agents/${AGENT_NAME}/threads/${threadId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: input }),
            });
            const data = await response.json();
            const assistantComposite = {
                role: "assistant",
                type: "drugs-composite",
                data,
            };
            setMessages((prev) => [...prev, assistantComposite]);
        } catch (error) {
            setMessages((prev) => [...prev, { role: "assistant", text: "Error: Could not send message." }]);
        }
        setLoading(false);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === "Enter" && !loading) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        navigate("/");
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <button onClick={handleLogout} style={styles.logoutButton}>Logout</button>
                <h2>Drugs</h2>
            </div>

            {/* The EvidenceDrawer component, which handles its own presentation */}
            <EvidenceDrawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                evidence={currentEvidence}
                evidenceIndex={currentEvidenceIndex}
            />

            <div style={styles.chatBox} ref={chatBoxRef}>
                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        style={{
                            ...styles.message,
                            ...(msg.role === "user" ? styles.userMessage : styles.assistantMessage),
                            alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                        }}
                    >
                        <strong>{msg.role === "user" ? "You" : "Agent"}:</strong>{" "}
                        {msg.role === "user" ? (
                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                        ) : msg.type === "drugs-composite" ? (
                            <DrugsOneBubbleWithEvidence
                                response={msg.data}
                                handleEvidenceClick={handleEvidenceClick}
                            />
                        ) : (
                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                        )}
                    </div>
                ))}

                {loading && (
                    <div style={{ alignSelf: "flex-start", color: "#888", marginTop: 8 }}>
                        <span className="loader" style={styles.loader}></span> Agent is typing...
                    </div>
                )}
            </div>

            <div style={styles.inputContainer}>
                <input
                    style={styles.input}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    disabled={loading}
                    onKeyDown={handleInputKeyDown}
                />
                <button
                    style={{
                        ...styles.button,
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                    }}
                    onClick={sendMessage}
                    disabled={loading}
                >
                    {loading ? "Sending..." : "Send"}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        maxWidth: 1100,
        width: "95%",
        margin: "30px auto",
        fontFamily: "Arial, sans-serif",
        padding: 20,
        border: "1px solid #ddd",
        borderRadius: 8,
        boxSizing: 'border-box',
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 10,
    },
    logoutButton: {
        padding: "6px 12px",
        background: "#dc3545",
        color: "#fff",
        border: "none",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 14,
    },
    chatBox: {
        display: "flex",
        flexDirection: "column",
        height: "70vh",
        border: "1px solid #ccc",
        borderRadius: 4,
        padding: 10,
        marginBottom: 10,
        background: "#f9f9f9",
        overflowY: "auto",
    },
    message: {
        maxWidth: "80%",
        padding: 8,
        borderRadius: 5,
        marginBottom: 6,
        whiteSpace: "pre-wrap",
        background: "#f1f1f1",
        color: "#222",
        border: "1px solid #e0e0e0",
    },
    userMessage: {
        background: "#d1f5e6",
        color: "#1a3c2b",
        border: "1px solid #7ed6a7",
    },
    assistantMessage: {
        background: "#e3f0fa",
        color: "#1a3556",
        border: "1px solid #7eb6e6",
    },
    inputContainer: {
        display: "flex",
        gap: 8,
    },
    input: {
        flex: 1,
        padding: 10,
        fontSize: 16,
        border: "1px solid #ccc",
        borderRadius: 4,
    },
    button: {
        padding: "10px 20px",
        background: "#007bff",
        color: "#fff",
        border: "none",
        borderRadius: 4,
        cursor: "pointer",
    },
    loader: {
        display: "inline-block",
        width: 16,
        height: 16,
        border: "2px solid #ccc",
        borderTop: "2px solid #007bff",
        borderRadius: "50%",
        marginRight: 8,
        verticalAlign: "middle",
    },
};

export default Drugs;
